import React from 'react';
import { CreateTabDesktopLayout } from './CreateTabDesktopLayout';
import { CreateTabMediumLayout } from './CreateTabMediumLayout';
import { CreateTabSmallLayout } from './CreateTabSmallLayout';
import { PlanNoStudentsEmptyState } from './PlanNoStudentsEmptyState';

/** Internal shared tree + panels for Create Units vs Add Topics — use CreateUnitsTabContent or AddTopicsTabContent. */
export interface PlanCurriculumTabContentProps {
    students: any[];
    selectedTopics: any[];
    expandedCards: Set<string>;
    expandedNodes: Set<string>;
    setExpandedNodes: (nodes: Set<string>) => void;
    curriculumData: any;
    tenantCustomCurriculumLibraryData?: any | null;
    isStudentCurriculumActive: boolean;
    /** Add Topics tab only: show Create Your Own (custom subject / topic / topics) in the library column. */
    showAddCustomMenu: boolean;
    /** Create Units tab: open Create Unit wizard from the student curriculum tree column. */
    onOpenCreateUnitWizard?: () => void;
    studentCurriculumData: any;
    setStudentCurriculumData?: (data: any) => void;
    selectedProvider: string;
    backendLessons: any[];
    selectedStudentPassFailThreshold: number | null;
    loadedNodeIds: Set<string>;
    loadingNodeIds: Set<string>;
    loadingTopicDetails: Set<string>;
    lessonsPanelRef: React.RefObject<HTMLDivElement>;
    mediumTreeviewRef: React.RefObject<HTMLDivElement>;
    onTreeNodeSelect: (node: any) => void;
    onLoadNodeChildren: (nodeId: string, nodeType: 'year' | 'topic') => void;
    onRemoveTopic: (index: number) => void;
    onToggleCard: (topicId: string) => void;
    onCreateAssessment: () => void;
    onOpenCreateYourOwnWizard: () => void;
    onRemoveTopicsFromStudentCurriculum: () => void;
    onAddTopics: () => void;
    /** Add Topics tab: delete custom catalog topics when selection is allowed (optional). */
    onDeleteTenantCustomCurriculumTopics?: () => void;
    buildBreadcrumb: (topic: any) => string;
    getProviderName: (providerKey: string | null | undefined) => string;
    handleLoadTopicDetails: (topicId: string, providerKey?: string) => void;
    setViewState: (state: number) => void;
    onClearAll: () => void;
    studentId?: string | null;
    isStudentLoading?: boolean;
    selectedStudentFirstName?: string;
    noStudentsDescription: string;
    onOpenUnitFromPlan?: (unit: any) => void;
}

export function PlanCurriculumTabContent({
    students,
    selectedTopics,
    expandedCards,
    expandedNodes,
    setExpandedNodes,
    curriculumData,
    tenantCustomCurriculumLibraryData,
    isStudentCurriculumActive,
    showAddCustomMenu,
    onOpenCreateUnitWizard,
    studentCurriculumData,
    setStudentCurriculumData,
    selectedProvider,
    backendLessons,
    selectedStudentPassFailThreshold,
    loadedNodeIds,
    loadingNodeIds,
    loadingTopicDetails,
    lessonsPanelRef,
    mediumTreeviewRef,
    onTreeNodeSelect,
    onLoadNodeChildren,
    onRemoveTopic,
    onToggleCard,
    onCreateAssessment,
    onOpenCreateYourOwnWizard,
    onRemoveTopicsFromStudentCurriculum,
    onAddTopics,
    onDeleteTenantCustomCurriculumTopics,
    buildBreadcrumb,
    getProviderName,
    handleLoadTopicDetails,
    setViewState,
    onClearAll,
    studentId,
    isStudentLoading = false,
    selectedStudentFirstName = '',
    noStudentsDescription,
    onOpenUnitFromPlan
}: PlanCurriculumTabContentProps) {
    if (isStudentLoading) {
        return (
            <div className="sm:h-[calc(100vh-16rem)]">
                <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue"></div>
                        <span className="text-gray-600">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (students.length === 0) {
        return (
            <PlanNoStudentsEmptyState
                description={noStudentsDescription}
                wrapperClassName="sm:h-[calc(100vh-16rem)]"
            />
        );
    }

    return (
        <div className="sm:h-[calc(100vh-16rem)]">
            <CreateTabDesktopLayout
                selectedTopics={selectedTopics}
                expandedCards={expandedCards}
                expandedNodes={expandedNodes}
                setExpandedNodes={setExpandedNodes}
                curriculumData={curriculumData}
                tenantCustomCurriculumLibraryData={tenantCustomCurriculumLibraryData}
                isStudentCurriculumActive={isStudentCurriculumActive}
                showAddCustomMenu={showAddCustomMenu}
                onOpenCreateUnitWizard={onOpenCreateUnitWizard}
                studentCurriculumData={studentCurriculumData}
                setStudentCurriculumData={setStudentCurriculumData}
                selectedProvider={selectedProvider}
                backendLessons={backendLessons}
                selectedStudentPassFailThreshold={selectedStudentPassFailThreshold}
                loadedNodeIds={loadedNodeIds}
                loadingNodeIds={loadingNodeIds}
                loadingTopicDetails={loadingTopicDetails}
                lessonsPanelRef={lessonsPanelRef}
                onTreeNodeSelect={onTreeNodeSelect}
                onLoadNodeChildren={onLoadNodeChildren}
                onRemoveTopic={onRemoveTopic}
                onToggleCard={onToggleCard}
                onCreateAssessment={onCreateAssessment}
                onOpenCreateYourOwnWizard={onOpenCreateYourOwnWizard}
                onRemoveTopicsFromStudentCurriculum={onRemoveTopicsFromStudentCurriculum}
                onAddTopics={onAddTopics}
                onDeleteTenantCustomCurriculumTopics={onDeleteTenantCustomCurriculumTopics}
                buildBreadcrumb={buildBreadcrumb}
                getProviderName={getProviderName}
                handleLoadTopicDetails={handleLoadTopicDetails}
                setViewState={setViewState}
                onClearAll={onClearAll}
                studentId={studentId}
                selectedStudentFirstName={selectedStudentFirstName}
                onOpenUnitFromPlan={onOpenUnitFromPlan}
            />

            <CreateTabMediumLayout
                selectedTopics={selectedTopics}
                expandedNodes={expandedNodes}
                setExpandedNodes={setExpandedNodes}
                curriculumData={curriculumData}
                tenantCustomCurriculumLibraryData={tenantCustomCurriculumLibraryData}
                isStudentCurriculumActive={isStudentCurriculumActive}
                showAddCustomMenu={showAddCustomMenu}
                onOpenCreateUnitWizard={onOpenCreateUnitWizard}
                studentCurriculumData={studentCurriculumData}
                setStudentCurriculumData={setStudentCurriculumData}
                selectedProvider={selectedProvider}
                backendLessons={backendLessons}
                selectedStudentPassFailThreshold={selectedStudentPassFailThreshold}
                loadedNodeIds={loadedNodeIds}
                loadingNodeIds={loadingNodeIds}
                mediumTreeviewRef={mediumTreeviewRef}
                onTreeNodeSelect={onTreeNodeSelect}
                onLoadNodeChildren={onLoadNodeChildren}
                onCreateAssessment={onCreateAssessment}
                onOpenCreateYourOwnWizard={onOpenCreateYourOwnWizard}
                onRemoveTopicsFromStudentCurriculum={onRemoveTopicsFromStudentCurriculum}
                onAddTopics={onAddTopics}
                onDeleteTenantCustomCurriculumTopics={onDeleteTenantCustomCurriculumTopics}
                setViewState={setViewState}
                studentId={studentId}
                selectedStudentFirstName={selectedStudentFirstName}
                onOpenUnitFromPlan={onOpenUnitFromPlan}
            />

            <CreateTabSmallLayout
                selectedTopics={selectedTopics}
                expandedNodes={expandedNodes}
                setExpandedNodes={setExpandedNodes}
                curriculumData={curriculumData}
                tenantCustomCurriculumLibraryData={tenantCustomCurriculumLibraryData}
                isStudentCurriculumActive={isStudentCurriculumActive}
                showAddCustomMenu={showAddCustomMenu}
                onOpenCreateUnitWizard={onOpenCreateUnitWizard}
                studentCurriculumData={studentCurriculumData}
                setStudentCurriculumData={setStudentCurriculumData}
                selectedProvider={selectedProvider}
                backendLessons={backendLessons}
                selectedStudentPassFailThreshold={selectedStudentPassFailThreshold}
                loadedNodeIds={loadedNodeIds}
                loadingNodeIds={loadingNodeIds}
                onTreeNodeSelect={onTreeNodeSelect}
                onLoadNodeChildren={onLoadNodeChildren}
                onCreateAssessment={onCreateAssessment}
                onOpenCreateYourOwnWizard={onOpenCreateYourOwnWizard}
                onRemoveTopicsFromStudentCurriculum={onRemoveTopicsFromStudentCurriculum}
                onAddTopics={onAddTopics}
                onDeleteTenantCustomCurriculumTopics={onDeleteTenantCustomCurriculumTopics}
                setViewState={setViewState}
                studentId={studentId}
                selectedStudentFirstName={selectedStudentFirstName}
                onOpenUnitFromPlan={onOpenUnitFromPlan}
            />
        </div>
    );
}
